import api from './api';

// ============================================
// Types
// ============================================

export interface MtgeDevice {
  id: string;
  serial_number: string;
  device_name: string;
  firmware_version?: string;
  agent_version?: string;
  wan_ip?: string;
  wan_cidr?: string;
  wan_gateway?: string;
  max_tenants: number;
  current_tenants: number;
  next_tenant_slot: number;
  base_wg_port: number;
  status: 'pending_approval' | 'approved' | 'provisioned' | 'active' | 'offline';
  registered_at?: string;
  last_seen?: string;
  created_at?: string;
  updated_at?: string;
}

export interface MtgeTenantAssignment {
  id: string;
  mtge_id: string;
  tenant_id: string;
  tenant_name?: string;
  vrf_id: number;
  tenant_slot: number;
  wg0_port: number;
  wg1_port: number;
  wg2_port: number;
  wg0_subnet?: string;
  wg1_subnet?: string;
  wg2_subnet?: string;
  internet_breakout: number;
  tunnel_protocol?: 'wireguard' | 'ikev2';
  tunnel_mode?: 'full' | 'split';
  onboard_status: 'pending' | 'provisioning' | 'active' | 'error' | 'decommissioned';
  sequence_number: number;
  last_config_hash?: string;
  is_dirty: boolean;
  sync_status: 'synced' | 'pending' | 'applying' | 'failed' | 'unknown';
  edge_acked_seq?: number;
  edge_acked_hash?: string;
  created_at?: string;
  updated_at?: string;
}

export interface MtgeCapacity {
  maxTenants: number;
  currentTenants: number;
  availableSlots: number;
  tenants: Array<{
    tenantId: string;
    tenantName: string;
    vrfId: number;
    status: string;
    syncStatus: string;
  }>;
}

export interface MtgeTenantConfigState {
  isDirty: boolean;
  syncStatus: string;
  sequenceNumber: number;
  lastConfigHash?: string;
  edgeAckedSeq?: number;
  edgeAckedHash?: string;
}

export interface MtgeListParams {
  search?: string;
  status?: string;
  page?: number;
  pageSize?: number;
  tenantId?: string;
}

export interface PaginatedMtgeResponse {
  items: MtgeDevice[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateMtgeData {
  deviceName: string;
  maxTenants?: number;
  baseWgPort?: number;
  wanIp?: string;
  wanCidr?: string;
  wanGateway?: string;
}

export interface UpdateMtgeData {
  deviceName?: string;
  maxTenants?: number;
  wanIp?: string;
  wanCidr?: string;
  wanGateway?: string;
  firmwareVersion?: string;
}

export interface AssignTenantData {
  tenantId: string;
  wg0Subnet?: string;
  wg1Subnet?: string;
  wg2Subnet?: string;
  internetBreakout?: boolean;
  tunnelProtocol?: 'wireguard' | 'ikev2';
}

export interface UpdateTenantAssignmentData {
  internetBreakout?: boolean;
  tunnelMode?: 'full' | 'split';
}

// ============================================
// Service
// ============================================

const mtgeService = {
  // ── Device CRUD ─────────────────────────────

  list: async (params?: MtgeListParams) => {
    const response = await api.get('/api/v2/mtges', { params }) as any;
    return response.data as PaginatedMtgeResponse;
  },

  getById: async (id: string) => {
    const response = await api.get(`/api/v2/mtges/${id}`) as any;
    return response.data as MtgeDevice;
  },

  create: async (data: CreateMtgeData) => {
    const response = await api.post('/api/v2/mtges', data) as any;
    return response.data as {
      id: string;
      serialNumber: string;
      deviceName: string;
      status: string;
      deploymentPassword: string;
      provisionToken?: string;
    };
  },

  update: async (id: string, data: UpdateMtgeData) => {
    const response = await api.put(`/api/v2/mtges/${id}`, data) as any;
    return response.data as MtgeDevice;
  },

  approve: async (id: string) => {
    const response = await api.post(`/api/v2/mtges/${id}/approve`) as any;
    return response.data as MtgeDevice;
  },

  // ── Capacity ────────────────────────────────

  getCapacity: async (id: string) => {
    const response = await api.get(`/api/v2/mtges/${id}/capacity`) as any;
    return response.data as MtgeCapacity;
  },

  // ── Tenant Assignments ──────────────────────

  listTenants: async (id: string) => {
    const response = await api.get(`/api/v2/mtges/${id}/tenants`) as any;
    return response.data as MtgeTenantAssignment[];
  },

  assignTenant: async (id: string, data: AssignTenantData) => {
    const response = await api.post(`/api/v2/mtges/${id}/tenants`, data) as any;
    return response.data as MtgeTenantAssignment;
  },

  delete: async (id: string) => {
    await api.delete(`/api/v2/mtges/${id}`);
  },

  removeTenant: async (id: string, tenantId: string) => {
    await api.delete(`/api/v2/mtges/${id}/tenants/${tenantId}`);
  },

  updateTenantAssignment: async (id: string, tenantId: string, data: UpdateTenantAssignmentData) => {
    const response = await api.put(`/api/v2/mtges/${id}/tenants/${tenantId}`, data) as any;
    return response.data as { tenantId: string; internetBreakout?: boolean; tunnelMode?: string };
  },

  getTenantDetail: async (id: string, tenantId: string) => {
    const response = await api.get(`/api/v2/mtges/${id}/tenants/${tenantId}`) as any;
    return response.data as MtgeTenantAssignment;
  },

  // ── Per-Tenant Config ───────────────────────

  syncTenantConfig: async (id: string, tenantId: string) => {
    const response = await api.post(`/api/v2/mtges/${id}/tenants/${tenantId}/sync`) as any;
    return response.data as { seqNum: number; configHash: string; commandCount: number; topic: string };
  },

  getTenantConfigState: async (id: string, tenantId: string) => {
    const response = await api.get(`/api/v2/mtges/${id}/tenants/${tenantId}/config`) as any;
    return response.data as MtgeTenantConfigState;
  },
};

export default mtgeService;
